import type { Column } from '@tanstack/react-table';
import { Loader2, PlusCircle, XCircle } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import type { AsyncFilterOptionsHook, Option } from '@/types/data-table';

interface DataTableAsyncFacetedFilterProps<TData, TValue> {
  column?: Column<TData, TValue>;
  title?: string;
  multiple?: boolean;
  useAsyncHook?: () => AsyncFilterOptionsHook;
}

/**
 * Async faceted filter component that fetches options from an API
 */
export function DataTableAsyncFacetedFilter<TData, TValue>({
  useAsyncHook,
  column,
  title,
  multiple = true,
}: DataTableAsyncFacetedFilterProps<TData, TValue>) {
  if (useAsyncHook === undefined) {
    throw new Error('useAsyncHook is required for async faceted filter');
  }
  const { t } = useTranslation('common');
  const { options, loading, error } = useAsyncHook();
  const [open, setOpen] = React.useState(false);
  const columnFilterValue = column?.getFilterValue();
  const selectedValues = new Set(Array.isArray(columnFilterValue) ? columnFilterValue : []);

  const onItemSelect = React.useCallback(
    (option: Option, isSelected: boolean) => {
      if (!column) return;

      if (multiple) {
        const newSelectedValues = new Set(selectedValues);
        if (isSelected) {
          newSelectedValues.delete(option.value);
        } else {
          newSelectedValues.add(option.value);
        }
        const filterValues = Array.from(newSelectedValues);
        column.setFilterValue(filterValues.length ? filterValues : undefined);
      } else {
        column.setFilterValue(isSelected ? undefined : [option.value]);
        setOpen(false);
      }
    },
    [column, multiple, selectedValues],
  );

  const onReset = React.useCallback(
    (event?: React.MouseEvent) => {
      event?.stopPropagation();
      column?.setFilterValue(undefined);
    },
    [column],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="border-dashed">
          {selectedValues?.size > 0 ? (
            <div
              role="button"
              aria-label={t('dataTable.filters.clearFilterAria', { title: title ?? '' })}
              tabIndex={0}
              onClick={onReset}
              className="focus-visible:ring-ring rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-1"
            >
              <XCircle />
            </div>
          ) : (
            <PlusCircle />
          )}
          {title}
          {selectedValues?.size > 0 && (
            <>
              <Separator orientation="vertical" className="mx-0.5 data-[orientation=vertical]:h-4" />
              <Badge variant="secondary" className="rounded-sm px-1 font-normal lg:hidden">
                {selectedValues.size}
              </Badge>
              <div className="hidden items-center gap-1 lg:flex">
                {selectedValues.size > 2 ? (
                  <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                    {t('dataTable.filters.countSelected', { count: selectedValues.size })}
                  </Badge>
                ) : (
                  options
                    ?.filter((option) => selectedValues.has(option.value))
                    .map((option) => (
                      <Badge variant="secondary" key={option.value} className="rounded-sm px-1 font-normal">
                        {option.label}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[12.5rem] p-0" align="start">
        <Command>
          <CommandInput placeholder={title} />
          <CommandList className="max-h-full">
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-muted-foreground ml-2 text-sm">
                  {t('dataTable.filters.loadingOptions')}
                </span>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center px-4 py-6">
                <span className="text-destructive text-sm">
                  {t('dataTable.filters.failedToLoadOptions')}
                </span>
                <span className="text-muted-foreground mt-1 text-xs">
                  {t('dataTable.filters.unknownError')}
                </span>
              </div>
            ) : options?.length === 0 ? (
              <CommandEmpty>{t('dataTable.filters.noOptionsFound')}</CommandEmpty>
            ) : (
              <>
                <CommandGroup className="max-h-[18.75rem] overflow-y-auto overflow-x-hidden">
                  {options?.map((option) => {
                    const isSelected = selectedValues.has(option.value);

                    return (
                      <CommandItem key={option.value} onSelect={() => onItemSelect(option, isSelected)}>
                        <Checkbox checked={isSelected} />
                        {option.icon && <option.icon />}
                        <span className="truncate">{option.label}</span>
                        {option.count && <span className="ml-auto font-mono text-xs">{option.count}</span>}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
                {selectedValues.size > 0 && (
                  <>
                    <CommandSeparator />
                    <CommandGroup>
                      <CommandItem onSelect={() => onReset()} className="justify-center text-center">
                        {t('dataTable.filters.clearFilters')}
                      </CommandItem>
                    </CommandGroup>
                  </>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
